<template>
    <EmailComposeModal
        v-if="modals.email_compose.is_open"
        :open="modals.email_compose.is_open"
        :reply-to-message="modals.email_compose.replyToMessage || null"
        :reply-to-conversation="modals.email_compose.replyToConversation || null"
        :prefilled-recipient="modals.email_compose.recipient || ''"
        :prefilled-recipients="modals.email_compose.recipients || []"
        :prefilled-subject="modals.email_compose.subject || ''"
        :creator-mode="modals.email_compose.creatorMode"
        @close="$store.commit('modal:close', 'email_compose')"
    />

    <ModalBasic
        :title="getFromDictionary(`list.modal.type.${modal.type}`, 'title')"
        :modal-open="modal.isOpen"
        @close-modal="modal.isOpen = false"
    >
        <div v-if="modal.type === 'access'">
            <div class="px-5 py-4">
                <ListAccessControls
                    :current-list="currentList"
                    :operators="potentialShares.operators"
                    :organizations="potentialShares.organizations"
                    @on-list-access="onShareList"
                />
            </div>
        </div>
    </ModalBasic>

    <CreatorSearchModal
        :modal-open="state.modals.addCreator.isOpen"
        :default-platform-id="filter.platformId"
        action-text="Add to List"
        :current-list-size="currentList?.totals?.total"
        @close-modal="state.modals.addCreator.isOpen = false"
        @submit="onCreatorAddSubmit"
    />

    <BulkImportCreatorsModal
        :modal-open="state.modals.bulkImportCreators.isOpen"
        :current-list-id="currentList.id"
        :table-creators="creators"
        :current-list-size="currentList?.totals?.total"
        :default-platform-id="filter.platformId"
        @close-modal="state.modals.bulkImportCreators.isOpen = false"
        @submit="onBulkImportSubmit"
    >
    </BulkImportCreatorsModal>

    <div class="col-span-full">
        <header class="relative rounded-sm border border-b border-slate-100 border-slate-200 bg-white px-3 py-2 mt-4 lg:mt-0">
            <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full">
                <div class="flex justify-center lg:inline-flex lg:justify-start w-full lg:w-auto rounded bg-slate-200 p-1 mb-4 lg:mb-0" role="group">
                    <button
                        class="flex items-center justify-center rounded px-5 py-2.5 text-sm font-semibold leading-tight transition duration-150 ease-in-out focus:outline-none focus:ring-0 hover:text-foreground"
                        :class="{
                            'bg-white text-foreground': filter.isCombined === true,
                            'text-slate-600': filter.isCombined !== true,
                        }"
                        @click.prevent="onCombinedClick()"
                    >
                        All
                    </button>

                    <button
                        class="flex items-center justify-center rounded px-5 py-2.5 text-sm font-bold leading-tight text-slate-900 transition duration-150 ease-in-out focus:outline-none focus:ring-0"
                        :class="{
                            'bg-white': filter.platformId === 'instagram',
                        }"
                        @click.prevent="onPlatformClick('instagram')"
                    >
                        <AppIcon class="h-4 w-4 text-instagram" name="instagram" />
                    </button>

                    <button
                        class="flex items-center justify-center rounded px-5 py-2.5 text-sm font-bold leading-tight text-slate-900 transition duration-150 ease-in-out focus:outline-none focus:ring-0"
                        :class="{
                            'bg-white': filter.platformId === 'tiktok',
                        }"
                        @click.prevent="onPlatformClick('tiktok')"
                    >
                        <AppIcon class="h-4 w-4 text-tiktok" name="tiktok" />
                    </button>

                    <button
                        class="flex items-center justify-center rounded px-5 py-2.5 text-sm font-bold leading-tight text-slate-900 transition duration-150 ease-in-out focus:outline-none focus:ring-0"
                        :class="{
                            'bg-white': filter.platformId === 'youtube',
                        }"
                        @click.prevent="onPlatformClick('youtube')"
                    >
                        <AppIcon class="h-4 w-4 text-youtube" name="youtube" />
                    </button>

                    <button
                        class="flex items-center justify-center rounded px-5 py-2.5 text-sm font-bold leading-tight text-slate-900 transition duration-150 ease-in-out focus:outline-none focus:ring-0"
                        :class="{
                            'bg-white': filter.platformId === 'twitch',
                        }"
                        @click.prevent="onPlatformClick('twitch')"
                    >
                        <AppIcon class="h-4 w-4 text-twitch" name="twitch" />
                    </button>

                    <button
                        class="flex items-center justify-center rounded px-5 py-2.5 text-sm font-bold leading-tight text-slate-900 transition duration-150 ease-in-out focus:outline-none focus:ring-0"
                        :class="{
                            'bg-white': filter.platformId === 'twitter',
                        }"
                        @click.prevent="onPlatformClick('twitter')"
                    >
                        <AppIcon class="h-4 w-4 text-x" name="x-twitter" />
                    </button>
                </div>

                <div class="flex flex-col gap-2 w-full lg:w-auto lg:flex-row lg:items-center lg:justify-end">
                    <div class="flex flex-row gap-2 w-full">
                        <div class="relative flex-grow">
                            <form class="relative w-full" @submit.prevent>
                                <label for="action-search" class="sr-only">Search</label>
                                <input
                                    id="action-search"
                                    v-model="filter.searchText"
                                    class="form-input pl-9 focus:border-slate-300 w-full"
                                    type="search"
                                    placeholder="Search..."
                                />
                                <div class="group absolute inset-0 right-auto" aria-label="Search">
                                    <svg
                                        class="ml-3 mr-2 mt-2.5 h-4 w-4 shrink-0 fill-current text-slate-400 group-hover:text-slate-500"
                                        viewBox="0 0 16 16"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M7 14c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7zM7 2C4.243 2 2 4.243 2 7s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z"
                                        />
                                        <path
                                            d="M15.707 14.293L13.314 11.9a8.019 8.019 0 01-1.414 1.414l2.393 2.393a.997.997 0 001.414 0 .999.999 0 000-1.414z"
                                        />
                                    </svg>
                                </div>
                            </form>
                        </div>
                        <div class="flex-shrink">
                            <ListsCreatorActionButtons
                                :list="currentList"
                                :platformId="filter.platformId"
                                @export-creators="onExportCreators"
                                @import-creators="state.modals.bulkImportCreators.isOpen = true"
                                @share="onModalOpen('access')"
                                @add-creators="state.modals.addCreator.isOpen = true"
                            ></ListsCreatorActionButtons>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add to campaign options -->
            <div v-show="selectedCreators.length" class="absolute left-0 top-0 flex h-full w-full bg-white p-3">
                <div class="my-auto flex gap-2 px-5">
                    <div class="mr-2 flex cursor-pointer text-slate-500 hover:text-slate-900" @click="onModalClose">
                        <AppIcon name="xmark" class="my-auto h-4 w-4 shrink-0" />
                    </div>

                    <button
                        class="btn btn-sm bg-indigo-500 text-white hover:bg-indigo-600"
                        @click="
                            $store.commit('modal:setData', {
                                modal_id: 'add_creator_to_modal',
                                data: {
                                    is_open: true,
                                    platform_id: filter.platformId,
                                    default_all_platforms: true,
                                    superviews: $lodash.map(selectedCreators, 'superview'),
                                },
                            })
                        "
                    >
                        Add selected to... ({{ selectedCreators.length }})
                    </button>

                    <button
                        class="btn btn-sm bg-indigo-500 text-white hover:bg-indigo-600"
                        @click="
                            $store.commit('modal:setData', {
                                modal_id: 'add_creator_to_modal',
                                data: {
                                    is_open: true,
                                    platform_id: filter.platformId,
                                    superviews: $lodash.map(creators, 'superview'),
                                },
                            })
                        "
                    >
                        Add all to... ({{ creators.length }})
                    </button>

                    <button class="btn btn-sm bg-red-500 text-white hover:bg-red-600" @click="onModalOpen('remove')">
                        Remove Selected from list ({{ selectedCreators.length }})
                    </button>
                </div>
            </div>
        </header>

        <template v-if="!filter.isCombined">
            <ListReport
                ref="listReport"
                :list="currentList"
                :platform-id="filter.platformId"
                :search-text="filter.searchText"
                :fixed-day-range="state.tab === 'creators'"
                :is-editable="isEditable"
                @remove-from-list="
                    (creatorsToRemove) => {
                        selectedCreators = creatorsToRemove
                        onRemoveFromList()
                    }
                "
                @send-email="openEmailCompose"
            >
            </ListReport>

        </template>

        <template v-if="filter.isCombined">
            <ListReportCombined
                ref="listReportCombined"
                :list="currentList"
                :search-text="filter.searchText"
            >
            </ListReportCombined>
        </template>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { nextTick } from 'vue'

import ModalBasic from '@/components/ModalBasic.vue'
import CreatorSearchModal from '@/partials/modals/CreatorSearchModal.vue'
import ListAccessControls from '@/components/ListAccessControls.vue'
import BulkImportCreatorsModal from '../../modals/BulkImportCreatorsModal.vue'
import ListsCreatorActionButtons from './ListsCreatorActionButtons.vue'
import ListReport from './ListReport.vue'
import ListReportCombined from './ListReportCombined.vue'
import EmailComposeModal from '@/partials/modals/EmailComposeModal.vue'

export default {
    name: 'ListsCreatorTable',

    components: {
        ModalBasic,
        CreatorSearchModal,
        BulkImportCreatorsModal,
        ListAccessControls,
        ListsCreatorActionButtons,
        ListReport,
        ListReportCombined,
        EmailComposeModal,
    },

    props: {
        creators: {
            type: Object,
        },
        supercampaigns: {
            type: Object,
        },
        currentList: {
            type: Object,
        },
        potentialShares: {
            operators: Object,
            organizations: Object,
        },
        defaultPlatformId: {
            type: String,
        },
        isEditable: {
            type: Boolean,
            default: true,
        },
    },

    emits: ['add-creators', 'bulk-import-creators', 'edit-creators', 'on-list-share', 'fetch-shared-to'],

    data() {
        return {
            selectedCreators: [],

            filter: {
                searchText: '',
                platformId: 'instagram',
                isCombined: false,
            },

            modal: {
                isOpen: false,
                type: 'remove',
            },

            state: {
                tab: 'creators',

                modals: {
                    addCreator: {
                        isOpen: false,
                    },
                    bulkImportCreators: {
                        isOpen: false,
                    },
                },
            },
        }
    },

    computed: {
        ...mapGetters(['getFromDictionary', 'isSfStaff', 'checkPermission', 'modals']),

        canGenerateReports() {
            return !(this.filter?.isCombined === true)
        },
    },

    watch: {
        defaultPlatformId(newValue, oldValue) {
            if (newValue !== oldValue) {
                nextTick(() => {
                    this.filter.platformId = newValue
                })
            }
        },
        currentList() {
            this.selectedCreators = []
            this.state.tab = 'creators'
        },
    },

    created() {
        if (this.defaultPlatformId) {
            this.filter.platformId = this.defaultPlatformId
        }
    },

    methods: {
        onPlatformClick(platform) {
            this.filter.platformId = platform
            this.filter.isCombined = false
            this.selectedCreators = []

            if (this.state.tab === 'report' && !this.canGenerateReports) {
                this.state.tab = 'creators'
            }
        },

        onCombinedClick() {
            this.filter.isCombined = true
            this.filter.platformId = null
            this.selectedCreators = []
            this.state.tab = 'creators'
        },

        onTabClick(tab) {
            this.state.tab = tab
        },

        onModalOpen(type) {
            this.modal.type = type
            this.modal.isOpen = true

            if (type === 'access') {
                this.$emit('fetch-shared-to')
            }
        },

        onModalClose() {
            this.modal.isOpen = false
            this.selectedCreators = []
        },

        onBulkImportSubmit(data) {
            this.$emit('bulk-import-creators', data)
        },

        onCreatorAddSubmit(creators) {
            this.state.modals.addCreator.isOpen = false

            this.$emit('add-creators', {
                creators: _.map(creators, (creator) => {
                    return {
                        platform_user_id: creator.superview._id,
                        platform_id: creator.superview.platform_id,
                        platform_user_name: creator?.superview?.normalized?.name,
                    }
                }),
            })

            // Set the platform tab to the first creator that was added
            const firstCreator = _.first(creators)
            this.filter.platformId = firstCreator.superview.platform_id
        },

        onRemoveFromList() {
            this.$emit('edit-creators', {
                type: 'remove-selected',
                creators: _.map(this.selectedCreators, (creator) => {
                    return {
                        platform_user_id: creator.id,
                    }
                }),
            })
        },

        openEmailCompose(recipients) {
            this.$store.commit('modal:setData', {
                modal_id: 'email_compose',
                data: {
                    is_open: true,
                    recipient: null,
                    recipients,
                    subject: null,
                    replyToMessage: null,
                    replyToConversation: null,
                    creatorMode: false,
                },
            })

            this.$store.commit('modal:open', 'email_compose')
        },

        onAddSelectedToCampaign({ campaign }) {
            this.$emit('edit-creators', {
                type: 'add-selected',
                creators: _.map(this.selectedCreators, 'list_creator'),
                campaign_id: campaign.id,
                supercampaign_id: campaign.parent_id,
            })

            this.selectedCreators = []
        },

        onAddAllToCampaign({ campaign }) {
            this.$emit('edit-creators', {
                type: 'add-all',
                creators: this.dataInView.creators,
                campaign_id: campaign.id,
                supercampaign_id: campaign.parent_id,
            })

            this.selectedCreators = []
        },

        onShareList({ list, type, sharedOperator, sharedOrganization }) {
            this.$emit('on-list-share', {
                list,
                type,
                sharedOperator,
                sharedOrganization,
            })
        },

        onExportCreators() {
            if (this.filter.isCombined) {
                this.$refs.listReportCombined.onExportCreators()
            }
            else {
                this.$refs.listReport.onExportCreators(this.selectedCreators)
            }
        },
    },
}
</script>
